import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  Paper,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { PageHeader } from "../../../components/ui";
import { listCardsPaged } from "../../../api/cards";
import { listBaseRawMaterialsPaged, receiveRawMaterial } from "../../../api/rawMaterials";
import { getMaterialVariantDimensions } from "../../../api/materials";
import { listDimensionOptions } from "../../../api/variantDimensions";
import ReferencePickerDialog from "../../../components/ReferencePickerDialog";

function normalizePaperForm(value) {
  const v = String(value || "")
    .trim()
    .toLowerCase();
  if (v === "bobin" || v === "tabaka") return v;
  return null;
}

function inferPaperFormFromMaterial(material) {
  const code = String(material?.code || "")
    .trim()
    .toLowerCase();
  const segments = code.split(".");
  const formCode = segments[2] || "";
  if (formCode === "99") return "bobin";
  if (formCode === "98") return "tabaka";
  return null;
}

function normalizeToken(value) {
  return String(value || "")
    .trim()
    .toLocaleLowerCase("tr-TR")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replaceAll("ı", "i")
    .replaceAll("ğ", "g")
    .replaceAll("ş", "s")
    .replaceAll("ö", "o")
    .replaceAll("ü", "u")
    .replaceAll("ç", "c");
}

function listMatchingDimensions(dims, { keyAliases = [], labelIncludes = [] }) {
  const normalizedAliases = keyAliases.map(normalizeToken).filter(Boolean);
  const normalizedLabelNeedles = labelIncludes.map(normalizeToken).filter(Boolean);

  const matches = [];
  for (const d of dims) {
    const dimKey = normalizeToken(d?.key);
    const dimLabel = normalizeToken(d?.label);

    const keyIdx = dimKey ? normalizedAliases.indexOf(dimKey) : -1;
    if (keyIdx >= 0) {
      matches.push({ d, score: keyIdx, tie: 0 });
      continue;
    }

    const labelIdx = normalizedLabelNeedles.findIndex((n) => (dimLabel || "").includes(n));
    if (labelIdx >= 0) {
      // label matches are lower priority than explicit key matches
      matches.push({ d, score: normalizedAliases.length + labelIdx, tie: 1 });
    }
  }

  return matches
    .sort((a, b) => {
      if (a.score !== b.score) return a.score - b.score;
      if (a.tie !== b.tie) return a.tie - b.tie;
      const aId = Number(a.d?.dimension_id || a.d?.id);
      const bId = Number(b.d?.dimension_id || b.d?.id);
      if (Number.isFinite(aId) && Number.isFinite(bId)) return aId - bId;
      return 0;
    })
    .map((x) => x.d);
}

function pickDimension(dims, criteria) {
  const list = listMatchingDimensions(dims, criteria);
  return list.length ? list[0] : null;
}

export default function PaperIntakePage() {
  const [selectedMaterial, setSelectedMaterial] = useState(null);

  const [materialId, setMaterialId] = useState("");
  const [templateDimensions, setTemplateDimensions] = useState([]);
  const [optionByDimensionId, setOptionByDimensionId] = useState({});
  const [paperKindOptionId, setPaperKindOptionId] = useState("");
  const [gramajG, setGramajG] = useState("");
  const [enM, setEnM] = useState("");
  const [mikronMm, setMikronMm] = useState("");

  const [supplierCardId, setSupplierCardId] = useState("");
  const [receivedByEmployeeCardId, setReceivedByEmployeeCardId] = useState("");
  const [supplierLabel, setSupplierLabel] = useState("");
  const [employeeLabel, setEmployeeLabel] = useState("");
  const [picker, setPicker] = useState(null);
  const [inboundDocNo, setInboundDocNo] = useState("");
  const [lotNo, setLotNo] = useState("");
  const lotNoInputRef = useRef(null);
  const [qty, setQty] = useState("");
  const [qtyKg, setQtyKg] = useState("");
  const [note, setNote] = useState("");
  const [unitCost, setUnitCost] = useState("");
  const [currency, setCurrency] = useState("TRY");
  const [priceUnit, setPriceUnit] = useState("");

  const [saving, setSaving] = useState(false);

  const loadPaperMaterials = useCallback(async ({ page, search }) => {
    const data = await listBaseRawMaterialsPaged("paper", { page, limit: 10, search });
    return { ...data, items: (data?.items || []).filter((item) => Boolean(inferPaperFormFromMaterial(item))) };
  }, []);
  const loadSuppliers = useCallback(({ page, search }) => listCardsPaged({
    type: "supplier", page, limit: 10, search,
  }), []);
  const loadEmployees = useCallback(({ page, search }) => listCardsPaged({
    type: "employee", page, limit: 10, search,
  }), []);

  const pickerConfig = picker === "material" ? {
    title: "Kağıt Stok Kartı Seç",
    searchPlaceholder: "Kod veya malzeme adı ile ara...",
    fetchPage: loadPaperMaterials,
    getPrimary: (item) => `${item.code || "-"} — ${item.name || "-"}`,
    getSecondary: (item) => inferPaperFormFromMaterial(item) === "bobin" ? "Bobin" : "Tabaka",
  } : picker === "supplier" ? {
    title: "Tedarikçi Seç",
    searchPlaceholder: "Tedarikçi adı ile ara...",
    fetchPage: loadSuppliers,
    getPrimary: (item) => item.name,
    getSecondary: (item) => item.code || "Tedarikçi kartı",
  } : picker === "employee" ? {
    title: "Teslim Alanı Seç",
    searchPlaceholder: "Personel adı ile ara...",
    fetchPage: loadEmployees,
    getPrimary: (item) => item.name,
    getSecondary: (item) => item.code || "Çalışan kartı",
  } : null;

  function selectReference(item) {
    if (picker === "material") {
      setMaterialId(String(item.id));
      setSelectedMaterial(item);
      setPaperKindOptionId("");
      setGramajG("");
      setEnM("");
      setMikronMm("");
      setLotNo("");
    }
    if (picker === "supplier") {
      setSupplierCardId(String(item.id));
      setSupplierLabel(item.name);
    }
    if (picker === "employee") {
      setReceivedByEmployeeCardId(String(item.id));
      setEmployeeLabel(item.name);
    }
    setPicker(null);
  }


  useEffect(() => {
    let cancelled = false;
    async function loadTemplate() {
      try {
        if (!materialId) {
          setTemplateDimensions([]);
          setOptionByDimensionId({});
          return;
        }
        const items = await getMaterialVariantDimensions(Number(materialId));
        if (!cancelled) setTemplateDimensions(Array.isArray(items) ? items : []);
      } catch (e) {
        console.error("MATERIAL TEMPLATE LOAD ERROR", e);
        if (!cancelled) setTemplateDimensions([]);
      }
    }
    loadTemplate();
    return () => {
      cancelled = true;
    };
  }, [materialId]);

  const paperFieldDimensions = useMemo(() => {
    const dims = Array.isArray(templateDimensions) ? templateDimensions : [];
    const formCandidates = listMatchingDimensions(dims, {
      keyAliases: ["paper_form", "kagit_formu", "form"],
      labelIncludes: ["hammadde formu", "form"],
    });
    const kindCandidates = listMatchingDimensions(dims, {
      keyAliases: ["paper_kind", "kind", "kagit_cinsi", "cins"],
      labelIncludes: ["kagit cinsi", "cins"],
    });
    const grammage = pickDimension(dims, {
      keyAliases: ["paper_gramaj_g", "gramaj", "grammage"],
      labelIncludes: ["gramaj", "grammage"],
    });
    const width = pickDimension(dims, {
      keyAliases: ["paper_en_m", "en", "paper_width_m"],
      labelIncludes: [" en", "genislik", "width"],
    });
    const micron = pickDimension(dims, {
      keyAliases: ["paper_mikron_mm", "mikron", "kalinlik"],
      labelIncludes: ["mikron", "kalinlik", "thickness"],
    });
    return { formCandidates, kindCandidates, grammage, width, micron };
  }, [templateDimensions]);

  const selectedOptionDimensions = useMemo(() => {
    const pickBestWithOptions = (candidates) => {
      const list = Array.isArray(candidates) ? candidates : [];
      if (!list.length) return null;
      for (const d of list) {
        const dimId = Number(d?.dimension_id || d?.id);
        if (!Number.isFinite(dimId)) continue;
        const opts = optionByDimensionId?.[dimId];
        if (Array.isArray(opts) && opts.length) return d;
      }
      return list[0];
    };
    return {
      form: pickBestWithOptions(paperFieldDimensions.formCandidates),
      kind: pickBestWithOptions(paperFieldDimensions.kindCandidates),
    };
  }, [paperFieldDimensions.formCandidates, paperFieldDimensions.kindCandidates, optionByDimensionId]);

  useEffect(() => {
    let cancelled = false;
    async function loadOptionDimensions() {
      const optionDims = [
        ...(paperFieldDimensions.formCandidates || []),
        ...(paperFieldDimensions.kindCandidates || []),
      ].filter((d) => Number.isFinite(Number(d?.dimension_id || d?.id)));

      // Dedup by dimension id
      const seen = new Set();
      const uniq = [];
      for (const d of optionDims) {
        const dimId = Number(d?.dimension_id || d?.id);
        if (!Number.isFinite(dimId)) continue;
        if (seen.has(dimId)) continue;
        seen.add(dimId);
        uniq.push(d);
      }

      const optionOnly = uniq.filter((d) => String(d?.value_type || "option") === "option");

      if (!optionOnly.length) {
        setOptionByDimensionId({});
        return;
      }
      if (!optionDims.length) {
        setOptionByDimensionId({});
        return;
      }
      try {
        const entries = await Promise.all(
          optionOnly.map(async (d) => {
            const dimId = Number(d.dimension_id || d.id);
            const opts = await listDimensionOptions(dimId);
            return [dimId, Array.isArray(opts) ? opts : []];
          })
        );
        if (cancelled) return;
        const next = {};
        for (const [dimId, opts] of entries) next[dimId] = opts;
        setOptionByDimensionId(next);
      } catch (err) {
        console.error("PAPER DIMENSION OPTIONS LOAD ERROR", err);
        if (!cancelled) setOptionByDimensionId({});
      }
    }
    loadOptionDimensions();
    return () => {
      cancelled = true;
    };
  }, [paperFieldDimensions.formCandidates, paperFieldDimensions.kindCandidates]);

  async function onSave() {
    const q = Number(qty);
    const enteredLotNo = String(lotNo || "").trim() || String(lotNoInputRef.current?.value || "").trim();
    const qKgRaw = String(qtyKg || "").trim();
    const qKg = qKgRaw === "" ? null : Number(qKgRaw);
    const gramajRaw = String(gramajG || "").trim();
    const enRaw = String(enM || "").trim();
    const mikronRaw = String(mikronMm || "").trim();
    const gramajNumber = gramajRaw === "" ? null : Number(gramajRaw);
    const enNumber = enRaw === "" ? null : Number(enRaw);
    const mikronNumber = mikronRaw === "" ? null : Number(mikronRaw);
    const formDimId = Number(selectedOptionDimensions.form?.dimension_id || selectedOptionDimensions.form?.id);
    const kindDimId = Number(selectedOptionDimensions.kind?.dimension_id || selectedOptionDimensions.kind?.id);
    const gramajDimId = Number(paperFieldDimensions.grammage?.dimension_id || paperFieldDimensions.grammage?.id);
    const enDimId = Number(paperFieldDimensions.width?.dimension_id || paperFieldDimensions.width?.id);
    const mikronDimId = Number(paperFieldDimensions.micron?.dimension_id || paperFieldDimensions.micron?.id);
    const paperForm = inferPaperFormFromMaterial(selectedMaterial);
    const mappedFormOption = formOptions.find(
      (o) => normalizePaperForm(o?.code || o?.label || "") === paperForm
    );
    const paperFormOptionId = mappedFormOption ? Number(mappedFormOption.id) : null;

    if (!materialId) {
      alert("Bobin/Tabaka stok kartını seçin");
      return;
    }
    if (!paperForm) {
      alert("Seçilen stok kartından bobin/tabaka bilgisi çözülemedi");
      return;
    }
    if (Number.isFinite(formDimId) && !paperFormOptionId) {
      alert("Varyantlarda bobin/tabaka seçeneği bulunamadı");
      return;
    }
    if (!supplierCardId) {
      alert("Tedarikçi seçin");
      return;
    }
    if (!inboundDocNo.trim()) {
      alert("İrsaliye numarası girin");
      return;
    }
    if (paperForm === "bobin" && !enteredLotNo) {
      alert("Lot No girin");
      return;
    }
    if (!receivedByEmployeeCardId) {
      alert("Teslim alan seçin");
      return;
    }
    if (!Number.isFinite(q) || q <= 0) {
      alert("Miktar pozitif olmalı");
      return;
    }
    if (qKg !== null && (!Number.isFinite(qKg) || qKg <= 0)) {
      alert("2. Birim (kg) pozitif sayı olmalı");
      return;
    }
    if (Number.isFinite(kindDimId) && selectedOptionDimensions.kind?.required && !paperKindOptionId) {
      alert("Kağıt cinsi seçin");
      return;
    }
    if (Number.isFinite(gramajDimId) && paperFieldDimensions.grammage?.required && (gramajNumber === null || !Number.isFinite(gramajNumber) || gramajNumber <= 0)) {
      alert("Gramaj pozitif sayı olmalı");
      return;
    }
    if (gramajNumber !== null && (!Number.isFinite(gramajNumber) || gramajNumber <= 0)) {
      alert("Gramaj pozitif sayı olmalı");
      return;
    }
    if (Number.isFinite(enDimId) && paperFieldDimensions.width?.required && (enNumber === null || !Number.isFinite(enNumber) || enNumber <= 0)) {
      alert("En pozitif sayı olmalı");
      return;
    }
    if (enNumber !== null && (!Number.isFinite(enNumber) || enNumber <= 0)) {
      alert("En pozitif sayı olmalı");
      return;
    }
    if (
      Number.isFinite(mikronDimId) &&
      paperFieldDimensions.micron?.required &&
      (mikronNumber === null || !Number.isFinite(mikronNumber) || mikronNumber <= 0)
    ) {
      alert("Mikron/kalınlık pozitif sayı olmalı");
      return;
    }
    if (mikronNumber !== null && (!Number.isFinite(mikronNumber) || mikronNumber <= 0)) {
      alert("Mikron/kalınlık pozitif sayı olmalı");
      return;
    }

    const selections = [];
    if (Number.isFinite(formDimId) && paperFormOptionId) {
      selections.push({ dimension_id: formDimId, option_id: paperFormOptionId });
    }
    if (Number.isFinite(kindDimId) && paperKindOptionId) {
      selections.push({ dimension_id: kindDimId, option_id: Number(paperKindOptionId) });
    }
    if (Number.isFinite(gramajDimId) && gramajNumber !== null && Number.isFinite(gramajNumber)) {
      selections.push({ dimension_id: gramajDimId, value_number: gramajNumber });
    }
    if (Number.isFinite(enDimId) && enNumber !== null && Number.isFinite(enNumber)) {
      selections.push({ dimension_id: enDimId, value_number: enNumber });
    }
    if (Number.isFinite(mikronDimId) && mikronNumber !== null && Number.isFinite(mikronNumber)) {
      selections.push({ dimension_id: mikronDimId, value_number: mikronNumber });
    }

    setSaving(true);
    try {
      const payload = {
        material_id: Number(materialId),
        paper_form: paperForm || null,
        selections,
        qty: q,
        qty_kg: qKg,
        note: note.trim() ? note.trim() : null,
        supplier_card_id: supplierCardId ? Number(supplierCardId) : null,
        inbound_doc_no: inboundDocNo.trim() ? inboundDocNo.trim() : null,
        lot_no: paperForm === "bobin" ? enteredLotNo : null,
        received_by_employee_card_id: receivedByEmployeeCardId ? Number(receivedByEmployeeCardId) : null,
        unit_cost: unitCost !== "" ? Number(unitCost) : null,
        currency: currency || "TRY",
        price_unit: priceUnit || null,
        // Bobin: coil physical data for coil_properties + yield planning
        ...(paperForm === "bobin" && {
          coil_length_mt: q,
          coil_width_cm: enNumber !== null && Number.isFinite(enNumber) ? enNumber : undefined,
          coil_weight_kg: qKg !== null ? qKg : undefined,
          coil_gsm: gramajNumber !== null && Number.isFinite(gramajNumber) ? gramajNumber : undefined,
        }),
      };
      const d = await receiveRawMaterial("paper", payload);
      alert(`Kaydedildi.\n\nLot: ${d.lot_no}\nBirim: ${d.unit || "-"}`);
      setQty("");
      setQtyKg("");
      setInboundDocNo("");
      setLotNo("");
      setReceivedByEmployeeCardId("");
      setNote("");
      setUnitCost("");
      setCurrency("TRY");
      setPriceUnit("");
    } catch (err) {
      console.error("PAPER RECEIVE ERROR", err);
      const apiErr = err?.response?.data?.error;
      const message = apiErr?.message || "Kaydetme başarısız";
      const sqlMessage = apiErr?.details?.sqlMessage;
      alert(sqlMessage ? `${message}\n\nDetay: ${sqlMessage}` : message);
    } finally {
      setSaving(false);
    }
  }

  const inferredPaperForm = inferPaperFormFromMaterial(selectedMaterial);
  const formDimId = Number(selectedOptionDimensions.form?.dimension_id || selectedOptionDimensions.form?.id);
  const kindDimId = Number(selectedOptionDimensions.kind?.dimension_id || selectedOptionDimensions.kind?.id);
  const formOptions = Number.isFinite(formDimId) ? optionByDimensionId[formDimId] || [] : [];
  const kindOptions = Number.isFinite(kindDimId) ? optionByDimensionId[kindDimId] || [] : [];
  const paperForm = inferredPaperForm;
  const qtyLabel =
    paperForm === "bobin" ? "Miktar (m)" : paperForm === "tabaka" ? "Miktar (adet)" : `Miktar (${selectedMaterial?.unit || "—"})`;

  return (
    <Stack spacing={2.5} sx={{ maxWidth: 1400, mx: "auto", "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#fff" } }}>
      <PageHeader
        title="Hammadde Giriş — Kağıt"
        subtitle="Önce bobin/tabaka stok kartını seçin, sonra kağıt cinsi ve teknik bilgileri girin."
      />

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 4, border: "1px solid #a5b4fc", bgcolor: "#f5f3ff", boxShadow: "0 10px 30px rgba(79, 70, 229, 0.10)" }}>
            <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>
              Kağıt Bilgileri
            </Typography>

            <Stack spacing={2}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>Bobin/Tabaka Stok Kartı</Typography>
                    <Button variant="outlined" fullWidth onClick={() => setPicker("material")} disabled={saving} sx={{ mt: 0.5, minHeight: 40, justifyContent: "flex-start", textTransform: "none", borderRadius: 2, color: materialId ? "#3730a3" : "text.secondary", fontWeight: materialId ? 700 : 400 }}>
                      {selectedMaterial ? `${selectedMaterial.code || "-"} — ${selectedMaterial.name || "-"}` : "Stok kartı seçiniz"}
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="paper-kind-label">Kağıt Cinsi</InputLabel>
                    <Select
                      labelId="paper-kind-label"
                      label="Kağıt Cinsi"
                      value={paperKindOptionId}
                      onChange={(e) => setPaperKindOptionId(String(e.target.value))}
                    >
                      <MenuItem value="">
                        <em>Seçiniz</em>
                      </MenuItem>
                      {!kindOptions.length ? (
                        <MenuItem value="" disabled>
                          Cins seçeneği yok
                        </MenuItem>
                      ) : null}
                      {kindOptions.map((o) => (
                        <MenuItem key={o.id} value={String(o.id)}>
                          {o.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Typography variant="caption" color="text.secondary">
                Form kodu doğrudan `materials.code` üzerinden çözülür: `150.01.99 = bobin`, `150.01.98 = tabaka`.
              </Typography>

              {!selectedMaterial ? <Alert severity="info">Stok kartını arayarak seçebilirsiniz.</Alert> : null}

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Gramaj"
                    value={gramajG}
                    onChange={(e) => setGramajG(e.target.value)}
                    inputMode="decimal"
                    InputProps={{
                      endAdornment: <InputAdornment position="end">g</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="En"
                    value={enM}
                    onChange={(e) => setEnM(e.target.value)}
                    inputMode="decimal"
                    InputProps={{
                      endAdornment: <InputAdornment position="end">cm</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Mikron/Kalınlık"
                    value={mikronMm}
                    onChange={(e) => setMikronMm(e.target.value)}
                    inputMode="decimal"
                    InputProps={{
                      endAdornment: <InputAdornment position="end">mm</InputAdornment>,
                    }}
                  />
                </Grid>
              </Grid>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 4, border: "1px solid #a5b4fc", bgcolor: "#f5f3ff", boxShadow: "0 10px 30px rgba(79, 70, 229, 0.10)" }}>
            <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>
              Stok Girişi
            </Typography>

            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>Tedarikçi</Typography>
                <Button variant="outlined" fullWidth onClick={() => setPicker("supplier")} disabled={saving} sx={{ mt: 0.5, minHeight: 40, justifyContent: "flex-start", textTransform: "none", borderRadius: 2, color: supplierCardId ? "#3730a3" : "text.secondary", fontWeight: supplierCardId ? 700 : 400 }}>
                  {supplierCardId ? supplierLabel : "Tedarikçi seçiniz"}
                </Button>
              </Box>

              <TextField size="small" label="İrsaliye Numarası" value={inboundDocNo} onChange={(e) => setInboundDocNo(e.target.value)} />

              <TextField
                size="small"
                label="Lot No"
                value={lotNo}
                onChange={(e) => setLotNo(e.target.value)}
                inputRef={lotNoInputRef}
                inputProps={{ maxLength: 100 }}
              />

              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>Teslim Alan</Typography>
                <Button variant="outlined" fullWidth onClick={() => setPicker("employee")} disabled={saving} sx={{ mt: 0.5, minHeight: 40, justifyContent: "flex-start", textTransform: "none", borderRadius: 2, color: receivedByEmployeeCardId ? "#3730a3" : "text.secondary", fontWeight: receivedByEmployeeCardId ? 700 : 400 }}>
                  {receivedByEmployeeCardId ? employeeLabel : "Teslim alanı seçiniz"}
                </Button>
              </Box>

              <TextField
                size="small"
                label={qtyLabel}
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                inputMode="decimal"
                InputProps={{
                  endAdornment:
                    paperForm === "bobin" ? (
                      <InputAdornment position="end">m</InputAdornment>
                    ) : paperForm === "tabaka" ? (
                      <InputAdornment position="end">adet</InputAdornment>
                    ) : (
                      <InputAdornment position="end">{selectedMaterial?.unit || "—"}</InputAdornment>
                    ),
                }}
              />
              <TextField
                size="small"
                label="2. Birim (kg)"
                value={qtyKg}
                onChange={(e) => setQtyKg(e.target.value)}
                inputMode="decimal"
              />
              <TextField size="small" label="Not (ops.)" value={note} onChange={(e) => setNote(e.target.value)} />

              <Stack direction="row" spacing={1}>
                <TextField
                  size="small"
                  label="Birim Fiyat"
                  value={unitCost}
                  onChange={(e) => setUnitCost(e.target.value)}
                  inputMode="decimal"
                  sx={{ flex: 2 }}
                />
                <FormControl size="small" sx={{ flex: 1, minWidth: 90 }}>
                  <InputLabel>Fiyatın Birimi</InputLabel>
                  <Select value={priceUnit} label="Fiyatın Birimi" onChange={(e) => setPriceUnit(e.target.value)}>
                    <MenuItem value="">
                      <em>—</em>
                    </MenuItem>
                    <MenuItem value="kg">kg</MenuItem>
                    <MenuItem value="adet">adet</MenuItem>
                    <MenuItem value="m">m</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ flex: 1, minWidth: 90 }}>
                  <InputLabel>Döviz</InputLabel>
                  <Select value={currency} label="Döviz" onChange={(e) => setCurrency(e.target.value)}>
                    <MenuItem value="TRY">TRY</MenuItem>
                    <MenuItem value="USD">USD</MenuItem>
                    <MenuItem value="EUR">EUR</MenuItem>
                  </Select>
                </FormControl>
              </Stack>

              <Button variant="contained" fullWidth disabled={saving} onClick={onSave} sx={{ mt: 0.5, py: 1.2, borderRadius: 2.25, textTransform: "none", fontWeight: 800, bgcolor: "#4f46e5", "&:hover": { bgcolor: "#3730a3" } }}>
                {saving ? "Kaydediliyor..." : "Kaydet"}
              </Button>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
      {pickerConfig ? <ReferencePickerDialog open={Boolean(picker)} {...pickerConfig} onClose={() => setPicker(null)} onSelect={selectReference} /> : null}
    </Stack>
  );
}

